import { Field } from 'formik';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';
import type { Map as ImmutableMap } from 'immutable';
import { List } from 'immutable';
import _get from 'lodash/get';
import React, { useCallback, useState } from 'react';

import { accountsSelectors } from 'app-components/flux/accounts';
import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { billerLoginsSelectors, billerLoginsUtils } from 'app-components/flux/biller-logins';
import { categoriesSelectors } from 'app-components/flux/categories';
import {
  chartOfAccountsSelectors,
  type chartOfAccountsTypes,
  chartOfAccountsUtils,
} from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import type {
  ScheduledTransaction,
  ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, type transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { getCommonConfig, tracker } from 'app-components/utils/core';

import InfoIcon from '@mui/icons-material/Info';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/system/Stack';

import type { Theme } from '@mui/material/styles';
import BillAmountField from 'components/BillPresentment/BillAmountField';
import DueDateField from 'components/BillPresentment/DueDateField';
import { HandleDisconnectOption } from 'components/BillPresentment/utils';
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import useQDialogs from 'components/QDialogs/useQDialogs';
import AccountField from 'components/QuickenControls/AccountField';
import MuiTagsField from 'components/QuickenControls/TagsField/TagsField';
import VisibilitySection from 'components/Transactions/VisibilitySection';
import TransferExclusion from 'components/Transactions/VisibilitySection/transferExclusion';
import UsageWithCategory from 'components/UsageWithCategory';

import { makeStyles } from 'tss-react/mui';
// import type { Im2JS } from 'types/shared';

import BillConnectButton from '../BillConnectButton';
import QFreqButton from '../QFreqButton';
import type { STFormikProps } from '../types';

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    height: '500px',
    '& .MuiTextField-root': {
      margin: 0,
      paddingBottom: 0,
      width: '100%',
    },
  },
  tabContent: {
    width: '100%',
    display: 'flex',
    gap: theme.spacing(3),
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    gap: theme.spacing(3),
    '> div:last-of-type': {
      paddingRight: 0,
      '&.MuiButtonBase-root': {
        margin: 0,
      },
    },
  },
  divider: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    padding: 16,
    marginTop: 24,
    marginBottom: 24,
    borderRadius: 12,
    border: `1px solid ${theme.palette.border.light}`,
  },
  width: { width: '50%' },
  amtTxt: {
    textAlign: 'left',
  },
  usageType: {
    maxWidth: 85,
  },
  accountField: {},
  bottomSection: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  flex: {
    display: 'flex',
  },
  bottomLeft: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 365,
    '.MuiFormControlLabel-root': {
      marginLeft: '6px',
    },
  },
  bottomRight: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  greyText: {
    color: theme.palette.text.secondary,
  },
  menuItem: {
    display: 'flex',
    color: theme.palette.text.primary,
    alignItems: 'center',
    padding: theme.shape.padding[2],
  },
  iconContainer: {
    marginRight: theme.shape.padding[2],
    color: theme.palette.text.secondary,
    minWidth: '24px !important',
  },
  redMenuItem: {
    display: 'flex',
    color: theme.palette.error.main,
    alignItems: 'center',
    padding: theme.shape.padding[2],
  },
  redIconContainer: {
    marginRight: theme.shape.padding[2],
    color: theme.palette.error.main,
    minWidth: '24px !important',
  },
  light: {
    height: 32,
    width: 32,
    padding: 4,
    opacity: 0.5,
    '&:hover': {
      opacity: 0.9,
      color: theme.palette.text.secondary,
    },
  },
  billerConnect: {
    marginBottom: 15,
    display: 'flex',
  },
  stack: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  errorBox: {
    width: '100%',
    paddingLeft: 20,
    flexShrink: 0,
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'relative',
    border: `1px solid ${theme.palette.warning.main}`,
    backgroundColor: theme.palette.warning.light,
    marginBottom: 15,
  },
  statusIcon: {
    height: 24,
    width: 24,
    marginRight: 8,
    color: theme.palette.warning.main,
    alignItems: 'flex-start',
  },
  inputLabel: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  billAmount: {
    paddingRight: 16,
  },
  matchCriteriaField: {
    backgroundColor: theme.palette.background.default,
  },
  infoIcon: {
    color: theme.palette.info.main,
    marginRight: 8,
    marginTop: 6,
    cursor: 'default',
    height: '16px',
    width: '16px',
  },
  learnMore: {
    flexDirection: 'row',
    display: 'flex',
  },
  innerCriteriaItem: {
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '219px',
    marginTop: 8,
  },
  learnMoreText: {
    whiteSpace: 'break-spaces',
  },
}));

const matchCriteriaOptions = {
  AUTO_MATCH: 'Auto Match',
  ANY_AMOUNT: 'Any Amount',
  EXACT_AMOUNT: 'Exact Amount',
  LIMITED_RANGE: 'Limited Range',
};

interface BasicTabProps {
  formikProps: STFormikProps;
  scheduledTransaction: ScheduledTransaction | ScheduledTransactionType;
  create?: boolean;
  subscriptionInitial?: boolean;
  setTabValue: (a: number) => void;
  canBVA?: boolean;
}

function BasicTab(props: BasicTabProps) {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const { dialogAlert } = useQDialogs();

  const { formikProps, scheduledTransaction, create, subscriptionInitial, setTabValue, canBVA } = props;

  const { values, touched, errors } = formikProps;
  const { transaction } = formikProps.values || {};
  const txnAmount = transaction.amount || 0;

  const featureFlags: ImmutableMap<string, any> = useAppSelector(featureFlagsSelectors.getFeatureFlags);

  const accountsById = useAppSelector(accountsSelectors.getAccountsById);
  const amountMatchVarianceFF = useAppSelector((state) =>
    featureFlagsSelectors.getFeatureFlags(state).get('matchAmountVariants'),
  );

  const connected = Boolean(
    scheduledTransaction?.billPresentmentAccountId && scheduledTransaction?.billPresentmentAccountId !== '0',
  );
  const billerAccount = useAppSelector(
    (state) =>
      connected && billerAccountsSelectors.getBillerAccountForId(state, scheduledTransaction?.billPresentmentAccountId),
  );
  const billConnected = Boolean(billerAccount);
  const billerLogin = useAppSelector(
    (state) => connected && billerLoginsSelectors.getBillerLoginForId(state, billerAccount?.billerLoginID),
  );

  const reconnect =
    scheduledTransaction?.billPresentmentAccountId && billerLogin && billerLoginsUtils.inError(billerLogin);

  // const [selectedCriteria, setSelectedCriteria] = useState(amountCriteria);
  const [focus, setFocus] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const variancePercent =
    create && amountMatchVarianceFF
      ? amountMatchVarianceFF
      : !create && values.matchCriteria === matchCriteriaOptions.LIMITED_RANGE
        ? scheduledTransaction?.matchAmountVariance
        : 15;

  const valTxn = {
    ...(scheduledTransaction?.transaction || {}),
    ...(transaction || {}),
  } as transactionsTypes.TransactionProps;
  const state = store.getState();
  const isCredit = valTxn && chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, valTxn.coa);
  const isIncome = transactionsSelectors.isIncomeTxn(state, valTxn);
  const isTransfer = chartOfAccountsUtils.isTransferCOA(valTxn.coa) || isCredit;

  const rangeValue = txnAmount * (variancePercent / 100);
  const limitedRangeAmount = txnAmount
    ? `$${Math.abs(txnAmount - rangeValue).toFixed(2)} - $${Math.abs(txnAmount + rangeValue).toFixed(2)}`
    : '$0 - $0';

  const matchCriteria = [
    {
      id: 'auto_match',
      title: matchCriteriaOptions.AUTO_MATCH,
      subTitle: 'Simplifi will optimize for you',
      showSeparator: true,
    },
    {
      id: 'any_amount',
      title: matchCriteriaOptions.ANY_AMOUNT,
    },
    {
      id: 'exact_amount',
      title: matchCriteriaOptions.EXACT_AMOUNT,
    },
    {
      id: 'limited_range',
      title: matchCriteriaOptions.LIMITED_RANGE,
      subTitle: limitedRangeAmount,
      showSeparator: true,
    },
    {
      id: 'info',
      title: 'These options let you control the amount allowed to match with this series.',
      subTitle: 'Learn More >',
      image: { icon: 'info' },
    },
  ];

  // If category opening balance or balance adjustment is selected, they should be ignored and unable to be changed back.
  const disableReports = chartOfAccountsUtils.disableExcludeReports(valTxn?.coa);

  //

  // @ts-expect-error FIXME TS7006
  const accountValidation = (value, valTxn) => {
    let errorMessage;
    if (!value || value.length <= 0) {
      errorMessage = 'Required';
    }

    if (valTxn?.coa && valTxn?.coa?.type === 'ACCOUNT' && value === valTxn?.coa?.id) {
      errorMessage = 'No self-transfers';
    }

    return errorMessage;
  };

  // @ts-expect-error FIXME TS7006
  const coaValidation = (coa, valTxn, state) => {
    let errorMessage;

    if (!coa) {
      errorMessage = 'Required';
    }

    if (transactionsUtils.txnHasUnsupportedTransfer({ coa }, featureFlags, state)) {
      errorMessage = 'Invalid transfer target';
    }

    if (coa && coa?.type === 'ACCOUNT' && valTxn.accountId === coa?.id) {
      errorMessage = 'No self-transfers';
    }

    return errorMessage;
  };

  const updateUsageAndCategory = async ({
    usage: newUsage,
    coa: newCoa,
  }: { usage: string; coa: chartOfAccountsTypes.ChartOfAccountProps }) => {
    const acc = accountsById.get(transaction?.accountId);
    const previousCoa = transaction?.coa;

    if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromF2S', true);
      formikProps.setFieldValue('transaction.excludeF2SDest', true);
    } else if (!acc?.isExcludedFromBudgets && chartOfAccountsUtils.isTransferCOA(previousCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromF2S', false);
      formikProps.setFieldValue('transaction.excludeF2SDest', false);
    }

    if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromReports', true);
      formikProps.setFieldValue('transaction.excludeReportsDest', true);
    } else if (!acc?.isExcludedFromReports && chartOfAccountsUtils.isTransferCOA(previousCoa)) {
      formikProps.setFieldValue('transaction.isExcludedFromReports', false);
      formikProps.setFieldValue('transaction.excludeReportsDest', false);
    }

    // bill + sub flag updates
    if (chartOfAccountsSelectors.isIncomeOrTransfer(state, newCoa)) {
      // force incomes and transfer to nothing
      formikProps.setFieldValue('transaction.isBill', false);
      formikProps.setFieldValue('transaction.isSubscription', false);

      /* if (chartOfAccountsSelectors.isCreditBill(state, valTxn.accountId, coa)) {
          formikProps.setFieldValue('transaction.isTransfer', false);
          formikProps.setFieldValue('transaction.isCredit', true);
        } else */
      if (chartOfAccountsUtils.isTransferCOA(newCoa)) {
        formikProps.setFieldValue('transaction.isTransfer', true);
        formikProps.setFieldValue('transaction.isIncome', false);
        //  formikProps.setFieldValue('transaction.isCredit', false);
      } else {
        formikProps.setFieldValue('transaction.isTransfer', false);
        formikProps.setFieldValue('transaction.isIncome', true);
      }
    } else if (subscriptionInitial) {
      // force subscription if entered from 'create subscription'
      formikProps.setFieldValue('transaction.isIncome', false);
      formikProps.setFieldValue('transaction.isTransfer', false);
      formikProps.setFieldValue('transaction.isBill', false);
      formikProps.setFieldValue('transaction.isSubscription', true);
    } else if (!valTxn.isBill && !valTxn.isSubscription) {
      // force bill if no value
      formikProps.setFieldValue('transaction.isBill', true);
      formikProps.setFieldValue('transaction.isSubscription', false);
      formikProps.setFieldValue('transaction.isIncome', false);
      formikProps.setFieldValue('transaction.isTransfer', false);
    }
    if (create && transaction?.amount < 0 && categoriesSelectors.isIncomeCat(null, newCoa.id)) {
      // on create, changed to income cat on create, flip sign
      formikProps.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    } else if (
      create &&
      categoriesSelectors.isIncomeCat(null, transaction.coa?.id || '') &&
      !categoriesSelectors.isIncomeCat(null, newCoa.id) &&
      transaction.amount > 0
    ) {
      // on create, changed from income to expense on create, flip sign back
      formikProps.setFieldValue('transaction.amount', Number(transaction.amount * -1));
    }
    await formikProps.setFieldValue('transaction.coa', newCoa);
    formikProps.setFieldValue('transaction.businessId', newUsage);
  };

  const startConnect = useCallback(() => {
    dispatch(doBillPresentment(scheduledTransaction, 'model edit'));
    setMenuAnchor(null);
    tracker.track(tracker.events.fixBillerStart, { initiator: 'model edit' });
  }, [dispatch, scheduledTransaction]);

  const unlink = () => {
    dialogAlert(
      'Disconnect biller',
      `Your ${scheduledTransaction?.transaction?.payee || billerAccount?.nickName || billerAccount?.name}
      bill reminders will no longer be updated with the latest bill information.`,
      // @ts-expect-error FIXME TS7006
      (btnObj) => {
        if (btnObj.btnPressed === 'Disconnect') {
          HandleDisconnectOption(dispatch, scheduledTransaction, billerLogin, 'model edit', true);
        }
      },
      ['Cancel', 'Disconnect'],
    );
  };

  // # ===================================================================================== #
  // # -----                                Renderers                                  ----- #
  // # ===================================================================================== #

  // @ts-expect-error FIXME TS7006
  const renderBPSFields = (formikProps, isCredit) =>
    billConnected && (
      <div className={classes.divider}>
        <div className={classes.billerConnect}>
          <Typography variant='bodyMedium' className={classes.tabContent}>
            Biller Connection
          </Typography>
          <ButtonBase
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            focusRipple
            style={{ borderRadius: 8 }}
          >
            <MoreHorizIcon className={classes.light} />
          </ButtonBase>
          {menuAnchor && (
            <Menu
              id='account-settings-menu'
              anchorEl={menuAnchor}
              keepMounted
              open
              onClose={() => setMenuAnchor(null)}
              onClick={(e) => e.stopPropagation()}
            >
              <MenuItem id='menu-connect' onClick={startConnect} classes={{ root: classes.menuItem }}>
                <ListItemIcon classes={{ root: classes.iconContainer }}>
                  <RestartAltIcon />
                </ListItemIcon>
                Reconnect biller
              </MenuItem>
              <Divider />
              <MenuItem id='menu-unlink' onClick={unlink} classes={{ root: classes.redMenuItem }}>
                <ListItemIcon classes={{ root: classes.redIconContainer }}>
                  <UnlinkIcon />
                </ListItemIcon>
                Disconnect biller
              </MenuItem>
            </Menu>
          )}
        </div>
        {reconnect && (
          <div className={classes.errorBox}>
            <div className={classes.stack}>
              <WarningRoundedIcon className={classes.statusIcon} />
              <Typography variant='paragraphRegular'>We&apos;re having trouble connecting to your biller.</Typography>
            </div>

            <QButton onClick={startConnect} id='series-connect'>
              Reconnect
            </QButton>
          </div>
        )}
        <Typography variant='paragraphRegular' className={classes.greyText}>
          When a new bill arrives, update reminder to
        </Typography>
        <div className={classes.flex}>
          <BillAmountField isCredit={isCredit} formikBag={formikProps} className={classes.billAmount} />
          <DueDateField formikBag={formikProps} />
        </div>
      </div>
    );

  // @ts-expect-error FIXME TS7006
  const renderDateAndFrequencySummary = (formikProps) => {
    const { virtualRecurrence, dueOn, virtualEndOn, virtualFrequency } = formikProps.values || {};
    const { text: summaryText } = customFrequencyUtils.recurrenceInfo({
      recurrence: virtualRecurrence,
      startDate: dueOn,
      endDate: virtualEndOn,
    });

    return (
      <QFreqButton
        // @ts-expect-error FIXME TS2322
        id='setDateAndFreqInitial'
        dueOn={dueOn}
        endOn={virtualEndOn}
        frequency={virtualFrequency}
        summaryText={summaryText}
        onClick={() => {
          tracker.track(tracker.events.viewFrequencyDetails);
          setTabValue(1);
        }}
      />
    );
  };

  // Main render

  return (
    <div className={classes.root}>
      <div className={classes.tabContent}>
        {/*
          // @ts-expect-error FIXME TS7006 */}
        <Field name='transaction.payee' validate={(value) => (value.length > 0 ? undefined : 'Required')}>
          {/*
            // @ts-expect-error FIXME TS7031 */}
          {({ field }) => (
            <TextField
              {...field}
              id='payee'
              label='Name'
              error={Boolean(_get(errors, field.name))}
              helperText={_get(errors, field.name)}
              InputProps={{
                autoComplete: 'off',
              }}
              variant='filled'
              margin='normal'
              fullWidth
              autoFocus
              onFocus={(event) => event.target.select()}
            />
          )}
        </Field>

        <div className={classes.row}>
          <Stack direction='row' display='flex' style={{ width: '100%' }}>
            <Field
              name='transaction.amount'
              // @ts-expect-error FIXME TS7006
              validate={(value) => (value !== undefined && value !== '' ? undefined : 'Required')}
            >
              {/*
                // @ts-expect-error FIXME TS7031 */}
              {({ field }) => (
                <QAmountField
                  {...field}
                  onChange={(event) =>
                    formikProps.setFieldValue(field.name, event.target.value === '' ? '' : Number(event.target.value))
                  }
                  expectedSign={isIncome ? 'positive' : 'negative'}
                  editable
                  id='schedtxn-amount'
                  label='Recurring Amount'
                  fullWidth
                  textFieldVariant='filled'
                  margin='normal'
                  autoComplete='one-time-code'
                  error={Boolean(_get(errors, field.name))}
                  classes={{ inputField: classes.amtTxt }}
                  className={classes.width}
                  placeholder='0.00'
                  helperText={_get(errors, field.name)}
                  InputProps={{
                    sx: {
                      borderTopRightRadius: '0px',
                      borderBottomRightRadius: '0px',
                      borderRightStyle: 'none',
                    },
                  }}
                />
              )}
            </Field>
            <Field name='matchCriteria'>
              {({ field: { ...field } }) => (
                <FormControl variant='filled' className={classes.width}>
                  <InputLabel className={classes.inputLabel}>Match Criteria</InputLabel>
                  <Select
                    value={field.value}
                    autoFocus={focus === field.name}
                    onChange={(event) => {
                      if (
                        event.target.value !==
                        'These options let you control the amount allowed to match with this series.'
                      ) {
                        formikProps.setFieldValue('matchCriteria', event.target.value);
                        // setSelectedCriteria(event.target.value as string);
                      }
                      if (event.target.value === matchCriteriaOptions.AUTO_MATCH) {
                        formikProps.setFieldValue('resetMatchAmountVariance', true);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }
                      if (event.target.value === matchCriteriaOptions.ANY_AMOUNT) {
                        formikProps.setFieldValue('matchAnyAmount', true);
                        formikProps.setFieldValue('resetMatchAmountVariance', true);
                      }
                      if (event.target.value === matchCriteriaOptions.EXACT_AMOUNT) {
                        formikProps.setFieldValue('matchAmountVariance', 0);
                        formikProps.setFieldValue('resetMatchAmountVariance', false);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }
                      if (event.target.value === matchCriteriaOptions.LIMITED_RANGE) {
                        formikProps.setFieldValue('matchAmountVariance', variancePercent);
                        formikProps.setFieldValue('resetMatchAmountVariance', false);
                        formikProps.setFieldValue('matchAnyAmount', false);
                      }

                      setFocus(field.name);
                    }}
                    sx={{
                      borderTopLeftRadius: '0px',
                      borderBottomLeftRadius: '0px',
                    }}
                    renderValue={() => <Typography variant='bodyRegular'>{field.value}</Typography>}
                    className={classes.matchCriteriaField}
                  >
                    {matchCriteria.map((item) => {
                      return (
                        <MenuItem key={item.id} value={item.title} divider={item.showSeparator}>
                          {item.id === 'info' ? (
                            <div className={classes.innerCriteriaItem}>
                              <div className={classes.learnMore}>
                                <InfoIcon fontSize='small' className={classes.infoIcon} />
                                <div>
                                  <Typography variant='captionRegular' className={classes.learnMoreText}>
                                    These options let you control the amount allowed to match with this series.
                                  </Typography>
                                  <br />
                                  <Typography
                                    variant='captionRegular'
                                    id='learn_more'
                                    color='primary'
                                    style={{ textTransform: 'capitalize' }}
                                    onClick={() => {
                                      window.open(getCommonConfig()?.external?.recurring_transactions);
                                    }}
                                  >
                                    Learn More {'>'}
                                  </Typography>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Typography variant='bodyRegular'>{item.title}</Typography>
                              {item.subTitle && (
                                <Typography variant='paragraphRegular' color={'textSecondary'}>
                                  {item.subTitle}
                                </Typography>
                              )}
                            </div>
                          )}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}
            </Field>
          </Stack>

          {/*
            // @ts-expect-error FIXME TS7006 */}
          <Field name='transaction.accountId' validate={(value) => accountValidation(value, valTxn)}>
            {({ field: { ...field } }) => (
              <AccountField
                {...field}
                key={_get(values, field.name)}
                // @ts-expect-error FIXME TS7006
                onSelected={(account) => {
                  if (account) {
                    // bugsnag check
                    const previousAcc = accountsById.get(transaction?.accountId);

                    if (account.isExcludedFromReports) {
                      formikProps.setFieldValue('transaction.isExcludedFromReports', true);
                      formikProps.setFieldValue('transaction.excludeReportsDest', true);
                    } else if (
                      !chartOfAccountsUtils.isTransferCOA(transaction.coa) &&
                      previousAcc?.isExcludedFromReports
                    ) {
                      formikProps.setFieldValue('transaction.isExcludedFromReports', false);
                      formikProps.setFieldValue('transaction.excludeReportsDest', false);
                    }
                    if (account.isExcludedFromBudgets) {
                      formikProps.setFieldValue('transaction.isExcludedFromF2S', true);
                      formikProps.setFieldValue('transaction.excludeF2SDest', true);
                    } else if (
                      !chartOfAccountsUtils.isTransferCOA(transaction.coa) &&
                      previousAcc?.isExcludedFromBudgets
                    ) {
                      formikProps.setFieldValue('transaction.isExcludedFromF2S', false);
                      formikProps.setFieldValue('transaction.excludeF2SDest', false);
                    }

                    if (!touched.transaction?.isBill && !touched.transaction?.isSubscription) {
                      if (chartOfAccountsSelectors.isBill(state, account.id, valTxn && valTxn.coa)) {
                        if (!valTxn.isSubscription) {
                          formikProps.setFieldValue('transaction.isBill', true);
                          formikProps.setFieldValue('transaction.isSubscription', false);
                        }
                      }
                    }
                    setFocus(field.name);
                    formikProps.setFieldValue(field.name, account ? account.id : undefined);
                  }
                }}
                initialAccountId={_get(values, field.name)}
                autoFocus={focus === field.name}
                id='account'
                label='Select Account'
                margin='normal'
                vairant='filled'
                autoComplete='one-time-code'
                fullWidth
                doNotIncludeClosed
                error={Boolean(_get(errors, field.name))}
                helperText={_get(errors, field.name)}
                className={classes.accountField}
              />
            )}
          </Field>
        </div>

        <div className={classes.row}>
          <Field name='transaction' validate={(transaction: any) => coaValidation(transaction.coa, valTxn, state)}>
            {/*
            // @ts-expect-error FIXME TS7031 */}
            {({ field }) => {
              const isError = Boolean(_get(touched, field.name) && _get(errors, field.name));
              let helperText = '';
              if (isError) {
                helperText = typeof _get(errors, field.name) === 'string' ? _get(errors, field.name) : '';
              }
              return (
                <UsageWithCategory
                  {...field}
                  accountId={field.value.accountId}
                  categoryProps={{
                    textFieldVariant: 'filled',
                    width: '260px',
                    label: 'Category',
                    editable: true,
                    value: field.value.coa,
                    error: Boolean(_get(touched, field.name) && _get(errors, field.name)),
                    helperText: helperText,
                  }}
                  onChange={updateUsageAndCategory}
                  usageProps={{
                    className: classes.usageType,
                    value: field.value.businessId,
                  }}
                />
              );
            }}
          </Field>

          <Field name='transaction.tags'>
            {/*
              // @ts-expect-error FIXME TS7031 */}
            {({ field }) => (
              <MuiTagsField
                {...field}
                value={Array.isArray(transaction?.tags) ? transaction?.tags : transaction?.tags?.toArray()}
                id='transaction-tags'
                onChange={(_e, tagArray, _reason, _details) => formikProps.setFieldValue(field.name, List(tagArray))}
              />
            )}
          </Field>
        </div>

        {/* Bill Connect stuff */}
        {renderBPSFields(formikProps, isCredit)}

        {renderDateAndFrequencySummary(formikProps)}

        {canBVA && <BillConnectButton onClick={() => setTabValue(2)} formikProps={formikProps} />}

        <Divider />

        <div className={classes.bottomSection}>
          <div className={classes.bottomLeft}>
            <Typography variant='paragraphRegular' className={classes.greyText}>
              Type
            </Typography>

            {isIncome || isCredit || isTransfer ? (
              <>
                {isIncome && 'Income'}
                {(isCredit || isTransfer) && 'Transfer'}
                {/* !isIncome && // TODO: Credit will be added in the future
                    <>
                      <Field name="transaction.isTransfer">
                        {({ field }) =>
                          <FormControlLabel
                            {...field}
                            control={<Radio />}
                            label="Transfer"
                            id="transfer-checkbox"
                            checked={Boolean(field.value)}
                            onChange={(event, checked) => {
                              field.onChange(event, Boolean(checked));
                              if (checked) {
                                formikProps.setFieldValue('transaction.isCredit', false);
                                formikProps.setFieldValue('transaction.isTransfer', true);
                              } else {
                                formikProps.setFieldValue('transaction.isCredit', true);
                              }
                            }}
                          />}
                      </Field>

                      <Field name="transaction.isCredit">
                        {({ field }) =>
                          <FormControlLabel
                            {...field}
                            control={<Radio />}
                            label="Credit card payment"
                            id="credit-checkbox"
                            checked={Boolean(field.value)}
                            onChange={(event, checked) => {
                              field.onChange(event, Boolean(checked));
                              if (checked) {
                                formikProps.setFieldValue('transaction.isTransfer', false);
                                formikProps.setFieldValue('transaction.isCredit', true);
                              } else {
                                formikProps.setFieldValue('transaction.isTransfer', true);
                              }
                            }}
                          />}
                      </Field>
                    </> */}
              </>
            ) : (
              <>
                {create && isIncome && (
                  <Field name='transaction.isIncome'>
                    {/*
                      // @ts-expect-error FIXME TS7031 */}
                    {({ field }) => (
                      <FormControlLabel
                        {...field}
                        control={<Radio />}
                        label='Income'
                        id='income-checkbox'
                        checked={Boolean(field.value)}
                        onChange={(event, checked) => {
                          field.onChange(event, Boolean(checked));
                          if (checked) {
                            formikProps.setFieldValue('transaction.isSubscription', false);
                            formikProps.setFieldValue('transaction.isBill', false);
                            formikProps.setFieldValue('transaction.isTransfer', false);
                            formikProps.setFieldValue('transaction.isIncome', true);
                          }
                        }}
                      />
                    )}
                  </Field>
                )}

                {!isIncome && (
                  <Field name='transaction.isBill'>
                    {/*
                      // @ts-expect-error FIXME TS7031 */}
                    {({ field }) => (
                      <FormControlLabel
                        {...field}
                        control={<Radio />}
                        label='Bill'
                        id='bill-checkbox'
                        checked={Boolean(field.value)}
                        onChange={(event, checked) => {
                          field.onChange(event, Boolean(checked));
                          if (checked) {
                            formikProps.setFieldValue('transaction.isSubscription', false);
                            formikProps.setFieldValue('transaction.isIncome', false);
                            formikProps.setFieldValue('transaction.isTransfer', false);
                            formikProps.setFieldValue('transaction.isBill', true);
                          }
                        }}
                      />
                    )}
                  </Field>
                )}

                {!isIncome && (
                  <Field name='transaction.isSubscription'>
                    {/*
                      // @ts-expect-error FIXME TS7031 */}
                    {({ field }) => (
                      <FormControlLabel
                        {...field}
                        control={<Radio />}
                        label='Subscription'
                        id='subscription-checkbox'
                        checked={Boolean(field.value)}
                        onChange={(event, checked) => {
                          field.onChange(event, Boolean(checked));
                          if (checked) {
                            formikProps.setFieldValue('transaction.isBill', false);
                            formikProps.setFieldValue('transaction.isIncome', false);
                            formikProps.setFieldValue('transaction.isTransfer', false);
                            formikProps.setFieldValue('transaction.isSubscription', true);
                          }
                        }}
                      />
                    )}
                  </Field>
                )}

                {create && isTransfer && (
                  <Field name='transaction.isTransfer'>
                    {/*
                      // @ts-expect-error FIXME TS7031 */}
                    {({ field }) => (
                      <FormControlLabel
                        {...field}
                        control={<Radio />}
                        label='Transfer'
                        id='transfer-checkbox'
                        checked={Boolean(field.value)}
                        onChange={(event, checked) => {
                          field.onChange(event, Boolean(checked));
                          if (checked) {
                            formikProps.setFieldValue('transaction.isSubscription', false);
                            formikProps.setFieldValue('transaction.isBill', false);
                            formikProps.setFieldValue('transaction.isIncome', false);
                            formikProps.setFieldValue('transaction.isTransfer', true);
                          }
                        }}
                      />
                    )}
                  </Field>
                )}
              </>
            )}
          </div>

          <div className={classes.bottomRight}>
            {valTxn?.coa?.type === 'ACCOUNT' ? (
              <TransferExclusion
                disabledReports={disableReports}
                excludeF2S={transaction.isExcludedFromF2S}
                setExcludeF2S={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromF2S', !transaction.isExcludedFromF2S)
                }
                excludeReports={transaction.isExcludedFromReports}
                setExcludeReports={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromReports', !transaction.isExcludedFromReports)
                }
                excludeReportsDest={transaction.excludeReportsDest}
                setExcludeReportsDest={() =>
                  formikProps.setFieldValue('transaction.excludeReportsDest', !transaction.excludeReportsDest)
                }
                excludeF2SDest={transaction.excludeF2SDest}
                setExcludeF2SDest={() =>
                  formikProps.setFieldValue('transaction.excludeF2SDest', !transaction.excludeF2SDest)
                }
              />
            ) : (
              <VisibilitySection
                disabledReports={disableReports}
                excludeF2S={transaction.isExcludedFromF2S}
                setExcludeF2S={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromF2S', !transaction.isExcludedFromF2S)
                }
                excludeReports={transaction.isExcludedFromReports}
                setExcludeReports={() =>
                  formikProps.setFieldValue('transaction.isExcludedFromReports', !transaction.isExcludedFromReports)
                }
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

BasicTab.displayName = 'stBasicTab';

export default BasicTab;
